/**
 * React Query Client Configuration
 * Centralized configuration for data fetching, caching, and mutations
 */

import { QueryClient } from '@tanstack/react-query'
import { HttpError } from '../http'
import { ApiError } from '../api/client'

/**
 * Global query client instance
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache fresh data for 30 seconds
      staleTime: 30_000,
      // Keep unused data in cache for 5 minutes
      gcTime: 5 * 60_000,
      // Refetch stale data on window focus
      refetchOnWindowFocus: false, // Too aggressive for our use case
      // Refetch on reconnect
      refetchOnReconnect: true,
      // Retry failed requests
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors (client errors)
        if (
          (error instanceof HttpError && error.status >= 400 && error.status < 500) ||
          (error instanceof ApiError && error.status >= 400 && error.status < 500)
        ) {
          return false
        }
        // Retry up to 2 times for 5xx errors
        return failureCount < 2
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
    },
    mutations: {
      // Don't retry mutations by default (user should explicitly retry)
      retry: false,
      // Global error handler for mutations
      onError: (error) => {
        if (error instanceof HttpError) {
          // Handle specific error codes
          if (error.status === 412) {
            console.error('[Mutation] Conflict detected (412) - ETag mismatch')
          } else if (error.status === 401) {
            console.error('[Mutation] Unauthorized (401) - token may be expired')
          } else if (error.status === 403) {
            console.error('[Mutation] Forbidden (403) - insufficient permissions')
          }
        }
      },
    },
  },
})
